import { Component, computed, inject, input, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from '../icon/icon.component';
import { NavigationItemInterface } from '../../models/navigation-item.interface';
import { LoggerService } from '../../services/logger.service';

@Component({
  selector: 'work-ui-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss'],
  standalone: true,
  imports: [RouterLink, IconComponent, TranslateModule],
})
export class BreadcrumbComponent implements OnInit, OnDestroy {
  @Input() rootBreadcrumb?: NavigationItemInterface;
  @Input() hideActiveBreadcrumb = false;
  /**
   * Zusätzliche Glieder, die vor dem aktiven Eintrag eingefügt werden — für Kontext, der nicht in der URL steckt
   * (z. B. der Kontakt eines Projekts).
   */
  public contextBreadcrumbs = input<NavigationItemInterface[]>([]);
  private readonly routeBreadcrumbs = signal<NavigationItemInterface[]>([]);
  protected readonly breadcrumbs = computed<NavigationItemInterface[]>(() => {
    const routeBreadcrumbs = this.routeBreadcrumbs();
    const contextBreadcrumbs = this.contextBreadcrumbs();
    if (!contextBreadcrumbs.length || !routeBreadcrumbs.length) {
      return [...routeBreadcrumbs, ...contextBreadcrumbs];
    }
    return [...routeBreadcrumbs.slice(0, -1), ...contextBreadcrumbs, routeBreadcrumbs[routeBreadcrumbs.length - 1]];
  });
  private breadcrumbsInProgress: NavigationItemInterface[] = [];
  private readonly router = inject(Router);
  private routerEventsSubscription: Subscription | undefined;

  /**
   * Listen for router events and create breadcrumbs.
   */
  ngOnInit(): void {
    // call on init
    this.createBreadcrumbs(this.router.routerState.root);
    this.routeBreadcrumbs.set(this.breadcrumbsInProgress);
    // listen for route changes
    this.routerEventsSubscription = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.breadcrumbsInProgress = [];
      this.createBreadcrumbs(this.router.routerState.root);
      if (
        this.rootBreadcrumb &&
        this.breadcrumbsInProgress.findIndex((breadcrumb) => breadcrumb.label === this.rootBreadcrumb?.label) === -1
      ) {
        this.breadcrumbsInProgress.unshift(this.rootBreadcrumb);
      }
      this.routeBreadcrumbs.set(this.breadcrumbsInProgress);
    });
  }

  ngOnDestroy() {
    if (this.routerEventsSubscription) {
      this.routerEventsSubscription.unsubscribe();
    }
  }

  /**
   * Loads the currently activated route, checks for children and builds the breadcrumb items.
   *
   * @param route
   * @private
   */
  private createBreadcrumbs(route: ActivatedRoute) {
    const children: ActivatedRoute[] = route.children || [];

    if (children.length === 0) {
      return;
    }

    for (const child of children) {
      this.getBreadcrumbForRouteUrls(child.snapshot);
    }
  }

  private getBreadcrumbForRouteUrls(snapshot: ActivatedRouteSnapshot, parentUrl = '', parentPrimaryUrl = '') {
    let url = parentUrl;
    let currentPrimaryUrl = parentPrimaryUrl;
    // Only append the route URL if we are in the primary outlet
    if (snapshot.outlet === 'primary') {
      const routeURL: string = snapshot.url.map((segment) => segment.path).join('/');
      if (routeURL !== '') {
        url += `/${routeURL}`;
        currentPrimaryUrl += `/${routeURL}`;
      }
    }

    try {
      const index = this.breadcrumbsInProgress.findIndex((breadcrumb) => breadcrumb.route === url);
      const routeIsTab = snapshot.routeConfig?.path?.includes(':tab');
      if (index === -1 && url !== '' && !routeIsTab) {
        this.breadcrumbsInProgress.push({
          label: snapshot.data['navigationItem']?.label || url,
          icon: snapshot.data['navigationItem']?.icon || null,
          route: url,
        });
      }
    } catch (noLabelOrIconForBreadcrumbException) {
      LoggerService.ERROR(
        this,
        'You have to add a breadcrumb badge and icon in the routing in the data attribute.',
        noLabelOrIconForBreadcrumbException,
      );
      return undefined;
    }

    // Check if current route ends with 'details' - if so, stop recursion
    const shouldStopAtDetails = url.endsWith('/details') || url.includes('/details/');

    if (shouldStopAtDetails) {
      return;
    }

    // Recursively process child routes, including named outlets
    for (const child of snapshot.children) {
      if (child.outlet === 'primary') {
        this.getBreadcrumbForRouteUrls(child, url, currentPrimaryUrl);
      } else {
        // Handle auxiliary routes (named outlets)
        const auxiliaryUrl = `${child.url.map((segment) => segment.path).join('/')}`;
        this.getBreadcrumbForRouteUrls(child, auxiliaryUrl, currentPrimaryUrl);
      }
    }
  }
}
